import { z } from 'zod';

/**
 * Client form schema for the web. Mirrors the server's
 * `CreateClientRequest`/`UpdateClientRequest` shape but flattens
 * `contact.{email,phone,website}` into top-level form fields so the
 * `<Field>` wrappers can render them as plain inputs.
 *
 * `defaultTaxExempt` and `isActive` are booleans bound to switches.
 */
export const clientFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  displayName: z.string().trim().max(30).optional().or(z.literal('')),
  company: z.string().trim().max(120).optional().or(z.literal('')),
  email: z
    .string()
    .trim()
    .email('Enter a valid email')
    .optional()
    .or(z.literal('')),
  phone: z.string().trim().max(40).optional().or(z.literal('')),
  website: z.string().trim().max(200).optional().or(z.literal('')),
  notes: z.string().trim().max(2000).optional().or(z.literal('')),
  taxId: z.string().trim().max(80).optional().or(z.literal('')),
  defaultTaxExempt: z.boolean(),
  isActive: z.boolean(),
});

export type ClientFormValues = z.infer<typeof clientFormSchema>;

export function clientFormDefaults(): ClientFormValues {
  return {
    name: '',
    displayName: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    notes: '',
    taxId: '',
    defaultTaxExempt: false,
    isActive: true,
  };
}

/**
 * Translate the form values back to the wire shape. Empty strings are
 * collapsed into omitted fields so we don't write empty strings into
 * the server's optional columns (which the server's Zod parsers
 * accept but the column intent is "absent").
 */
export function clientFormToWire(values: ClientFormValues) {
  const contact: Record<string, string> = {};
  if (values.email) contact.email = values.email;
  if (values.phone) contact.phone = values.phone;
  if (values.website) contact.website = values.website;

  return {
    name: values.name,
    displayName: values.displayName || undefined,
    company: values.company || undefined,
    contact,
    notes: values.notes || undefined,
    taxId: values.taxId || undefined,
    defaultTaxExempt: values.defaultTaxExempt,
    isActive: values.isActive,
  };
}

/**
 * Lift a wire `Client` row into form values. Used by the edit screen.
 */
export function clientFromWire(client: {
  name: string;
  displayName?: string | null;
  company?: string | null;
  contact?: Record<string, unknown> | null;
  notes?: string | null;
  taxId?: string | null;
  defaultTaxExempt: boolean;
  isActive: boolean;
}): ClientFormValues {
  const c = client.contact ?? {};
  return {
    name: client.name,
    displayName: client.displayName ?? '',
    company: client.company ?? '',
    email: typeof c.email === 'string' ? c.email : '',
    phone: typeof c.phone === 'string' ? c.phone : '',
    website: typeof c.website === 'string' ? c.website : '',
    notes: client.notes ?? '',
    taxId: client.taxId ?? '',
    defaultTaxExempt: client.defaultTaxExempt,
    isActive: client.isActive,
  };
}
